/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable no-use-before-define */

import { Matrix3 } from '../matrix/Matrix3';
import type { Matrix4 } from '../matrix/Matrix4';
import { Vector3 } from '../vector/Vector3';
import type { Box3 } from './Box3';
import type { Line3 } from './Line3';
import type { Sphere } from './Sphere';

export class Plane {
  public normal: Vector3;
  public constant: number;

  constructor(normal?: Vector3, constant?: number) {
    // normal is assumed to be normalized
    this.normal = normal !== undefined ? normal : new Vector3(1, 0, 0);
    this.constant = constant !== undefined ? constant : 0;
  }

  public set(normal: Vector3, constant: number): this {
    this.normal.copy(normal);
    this.constant = constant;

    return this;
  }

  public setComponents(x: number, y: number, z: number, w: number): this {
    this.normal.set(x, y, z);
    this.constant = w;

    return this;
  }

  public setFromNormalAndCoplanarPoint(normal: Vector3, point: Vector3): this {
    this.normal.copy(normal);
    this.constant = -point.dot(this.normal);

    return this;
  }

  public setFromCoplanarPoints(a: Vector3, b: Vector3, c: Vector3): this {
    const normal = v1.subVectors(c, b).cross(v2.subVectors(a, b)).normalize();

    // Q: should an error be thrown if normal is zero (e.g. degenerate plane)?

    this.setFromNormalAndCoplanarPoint(normal, a);

    return this;
  }

  public clone(): Plane {
    return new Plane().copy(this);
  }

  public copy(plane: Plane): this {
    this.normal.copy(plane.normal);
    this.constant = plane.constant;

    return this;
  }

  public normalize(): this {
    // Note: will lead to a divide by zero if the plane is invalid.

    const inverseNormalLength = 1.0 / this.normal.length();

    this.normal.multiplyScalar(inverseNormalLength);
    this.constant *= inverseNormalLength;

    return this;
  }

  public negate(): this {
    this.constant *= -1;
    this.normal.negate();

    return this;
  }

  public distanceToPoint(point: Vector3): number {
    return this.normal.dot(point) + this.constant;
  }

  public distanceToSphere(sphere: Sphere): number {
    return this.distanceToPoint(sphere.center) - sphere.radius;
  }

  public projectPoint(point: Vector3, out: Vector3): Vector3 {
    return out
      .copy(this.normal)
      .multiplyScalar(-this.distanceToPoint(point))
      .add(point);
  }

  public intersectLine(line: Line3, out: Vector3): Vector3 | undefined {
    const direction = line.delta(v1);

    const denominator = this.normal.dot(direction);

    if (denominator === 0) {
      // line is coplanar, return origin
      if (this.distanceToPoint(line.start) === 0) {
        return out.copy(line.start);
      }

      // Unsure if this is the correct method to handle this case.
      return undefined;
    }

    const t = -(line.start.dot(this.normal) + this.constant) / denominator;

    if (t < 0 || t > 1) {
      return undefined;
    }

    return out.copy(direction).multiplyScalar(t).add(line.start);
  }

  public intersectsLine(line: Line3): boolean {
    // Note: this tests if a line intersects the plane, not whether it (or its end-points) are coplanar with it.

    const startSign = this.distanceToPoint(line.start);
    const endSign = this.distanceToPoint(line.end);

    return (startSign < 0 && endSign > 0) || (endSign < 0 && startSign > 0);
  }

  public intersectsBox(box: Box3): boolean {
    return box.intersectsPlane(this);
  }

  public intersectsSphere(sphere: Sphere): boolean {
    return sphere.intersectsPlane(this);
  }

  public coplanarPoint(out: Vector3): Vector3 {
    return out.copy(this.normal).multiplyScalar(-this.constant);
  }

  public applyMatrix4(matrix: Matrix4, optionalNormalMatrix?: Matrix3): this {
    const normalMatrix = optionalNormalMatrix || m1.getNormalMatrix(matrix);

    const referencePoint = this.coplanarPoint(v1).applyMatrix4(matrix);

    const normal = this.normal.applyMatrix3(normalMatrix).normalize();

    this.constant = -referencePoint.dot(normal);

    return this;
  }

  public translate(offset: Vector3): this {
    this.constant -= offset.dot(this.normal);

    return this;
  }

  public equals(plane: Plane): boolean {
    return plane.normal.equals(this.normal) && plane.constant === this.constant;
  }
}

const v1 = new Vector3();
const v2 = new Vector3();
const m1 = new Matrix3();
